// Media API Service
import { createApiClient } from '../utils/axiosConfig';
import { getApiBaseUrl } from '../utils/apiConfig';
import { getToken } from '../utils/tokenHelper';

const API_BASE_URL = getApiBaseUrl();

// Create axios instance with default config and auth error handling
// Note: mediaApi uses /api prefix in baseURL
const apiClient = createApiClient(`${API_BASE_URL}/api`);

export interface MediaAsset {
  _id: string;
  userId: string;
  name: string;
  type: 'image' | 'video' | 'document' | 'audio';
  url: string;
  size: number;
  format?: string;
  width?: number;
  height?: number;
  usage: string;
  rights: 'Full usage rights' | 'Limited usage rights' | 'Editorial use only';
  downloads: number;
  tags?: string[];
  description?: string;
  createdAt: string;
  updatedAt: string;
  formattedSize?: string;
}

export interface CreateMediaAssetRequest {
  name: string;
  type: 'image' | 'video' | 'document' | 'audio';
  usage?: string;
  rights?: 'Full usage rights' | 'Limited usage rights' | 'Editorial use only';
  description?: string;
  tags?: string[];
}

export const getMediaAssets = async (type?: string): Promise<MediaAsset[]> => {
  try {
    const params = type && type !== 'all' ? { type } : {};
    const response = await apiClient.get('/media', { params });
    return response.data.assets || [];
  } catch (error: any) {
    console.error('Error fetching media assets:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch media assets');
  }
};

export const getMediaAsset = async (id: string): Promise<MediaAsset> => {
  try {
    const response = await apiClient.get(`/media/${id}`);
    return response.data.asset;
  } catch (error: any) {
    console.error('Error fetching media asset:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch media asset');
  }
};

export const uploadMediaAsset = async (
  file: File,
  data: CreateMediaAssetRequest
): Promise<MediaAsset> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', data.name);
    formData.append('type', data.type);
    if (data.usage) formData.append('usage', data.usage);
    if (data.rights) formData.append('rights', data.rights);
    if (data.description) formData.append('description', data.description);
    if (data.tags) formData.append('tags', JSON.stringify(data.tags));

    // Use shared apiClient (auth header added via interceptor)
    const response = await apiClient.post('/media/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data.asset;
  } catch (error: any) {
    console.error('Error uploading media asset:', error);
    throw new Error(error.response?.data?.message || 'Failed to upload media asset');
  }
};

export const updateMediaAsset = async (
  id: string,
  data: Partial<CreateMediaAssetRequest>
): Promise<MediaAsset> => {
  try {
    const response = await apiClient.put(`/media/${id}`, data);
    return response.data.asset;
  } catch (error: any) {
    console.error('Error updating media asset:', error);
    throw new Error(error.response?.data?.message || 'Failed to update media asset');
  }
};

export const deleteMediaAsset = async (id: string): Promise<void> => {
  try {
    await apiClient.delete(`/media/${id}`);
  } catch (error: any) {
    console.error('Error deleting media asset:', error);
    throw new Error(error.response?.data?.message || 'Failed to delete media asset');
  }
};

export const downloadMediaAsset = async (id: string): Promise<void> => {
  try {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/api/media/${id}/download`, {
      method: 'POST',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      }
    });

    if (!response.ok) {
      throw new Error('Failed to download media asset');
    }

    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'download';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
        // Decode URI component if needed
        try {
          filename = decodeURIComponent(filename);
        } catch (e) {
          // If decoding fails, use as is
        }
      }
    }

    // Get file blob
    const blob = await response.blob();
    
    // Create download link and trigger download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    console.error('Error downloading media asset:', error);
    throw new Error(error.message || 'Failed to download media asset');
  }
};

export default {
  getMediaAssets,
  getMediaAsset,
  uploadMediaAsset,
  updateMediaAsset,
  deleteMediaAsset,
  downloadMediaAsset
};

